import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f9fafb',
  green:  '#16a34a',
  orange: '#d97706',
};



/* ── Rich text toolbar ── */
function RichEditor({ value, onChange, placeholder, minHeight = 140 }) {
  const ref = useRef(null);

  function exec(cmd, val) {
    ref.current?.focus();
    document.execCommand(cmd, false, val || null);
    if (onChange) onChange(ref.current?.innerHTML || '');
  }

  const btnStyle = (active) => ({
    border: 'none', background: active ? T.bg : 'transparent', cursor: 'pointer',
    padding: '4px 7px', borderRadius: 4, fontSize: 13, fontWeight: 700, color: T.ink,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '6px 10px', borderBottom: `1px solid ${T.border}`, background: 'white', flexWrap: 'wrap' }}>
        <select onChange={e => exec('fontSize', e.target.value)} defaultValue="3"
          style={{ border: `1px solid ${T.border}`, borderRadius: 5, padding: '3px 6px', fontSize: 12, color: T.ink, cursor: 'pointer', background: 'white' }}>
          <option value="1">10px</option>
          <option value="2">12px</option>
          <option value="3">14px</option>
          <option value="4">16px</option>
          <option value="5">18px</option>
        </select>
        <button onMouseDown={e => { e.preventDefault(); exec('bold'); }} style={btnStyle(false)}><b>B</b></button>
        <button onMouseDown={e => { e.preventDefault(); exec('italic'); }} style={{ ...btnStyle(false), fontStyle: 'italic' }}>I</button>
        <button onMouseDown={e => { e.preventDefault(); exec('underline'); }} style={{ ...btnStyle(false), textDecoration: 'underline' }}>U</button>
        <button onMouseDown={e => { e.preventDefault(); exec('strikeThrough'); }} style={{ ...btnStyle(false), textDecoration: 'line-through' }}>S</button>
        <div style={{ width: 1, height: 16, background: T.border, margin: '0 2px' }} />
        <button onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }} style={btnStyle(false)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>
        </button>
        <button onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} style={btnStyle(false)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="9" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="9" y1="18" x2="21" y2="18"/><circle cx="4" cy="6" r="1.5" fill="currentColor"/><circle cx="4" cy="12" r="1.5" fill="currentColor"/><circle cx="4" cy="18" r="1.5" fill="currentColor"/></svg>
        </button>
        <button onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }} style={btnStyle(false)} title="Formatierung entfernen">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="4" x2="18" y2="4"/><line x1="6" y1="20" x2="10" y2="20"/><path d="M14 4l-4 16"/><line x1="3" y1="21" x2="21" y2="3"/></svg>
        </button>
      </div>
      {/* Editable area */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange && onChange(ref.current?.innerHTML || '')}
        data-placeholder={placeholder}
        style={{
          minHeight, padding: '12px 14px', outline: 'none', fontSize: 14,
          color: T.ink, lineHeight: 1.6, background: 'white',
          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        }}
        dangerouslySetInnerHTML={value !== undefined ? undefined : undefined}
      />
    </div>
  );
}

/* ── Simple text editor (wraps RichEditor with state) ── */
function Editor({ betreff, setBetreff, inhalt, setInhalt, showBetreff = true, placeholder = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && inhalt === '' && ref.current.innerHTML !== '') {
      ref.current.innerHTML = '';
    }
    if (ref.current && inhalt && ref.current.innerHTML !== inhalt) {
      ref.current.innerHTML = inhalt;
    }
  }, []);

  function exec(cmd, val) {
    ref.current?.focus();
    document.execCommand(cmd, false, val || null);
  }

  const btnStyle = {
    border: 'none', background: 'transparent', cursor: 'pointer',
    padding: '4px 7px', borderRadius: 4, fontSize: 13, fontWeight: 700, color: T.ink,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {showBetreff && (
        <input
          value={betreff}
          onChange={e => setBetreff(e.target.value)}
          placeholder="Betreff"
          style={{
            width: '100%', padding: '11px 14px', border: 'none', borderBottom: `1px solid ${T.border}`,
            fontSize: 14, color: T.ink, outline: 'none', boxSizing: 'border-box',
          }}
        />
      )}
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '6px 10px', borderBottom: `1px solid ${T.border}`, background: 'white', flexWrap: 'wrap' }}>
        <select onChange={e => exec('fontSize', e.target.value)} defaultValue="3"
          style={{ border: `1px solid ${T.border}`, borderRadius: 5, padding: '3px 6px', fontSize: 12, color: T.ink, cursor: 'pointer', background: 'white' }}>
          <option value="1">10px</option><option value="2">12px</option><option value="3">14px</option><option value="4">16px</option><option value="5">18px</option>
        </select>
        <button onMouseDown={e => { e.preventDefault(); exec('bold'); }} style={btnStyle}><b>B</b></button>
        <button onMouseDown={e => { e.preventDefault(); exec('italic'); }} style={{ ...btnStyle, fontStyle: 'italic' }}>I</button>
        <button onMouseDown={e => { e.preventDefault(); exec('underline'); }} style={{ ...btnStyle, textDecoration: 'underline' }}>U</button>
        <button onMouseDown={e => { e.preventDefault(); exec('strikeThrough'); }} style={{ ...btnStyle, textDecoration: 'line-through' }}>S</button>
        <div style={{ width: 1, height: 16, background: T.border, margin: '0 2px' }} />
        <button onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }} style={btnStyle}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>
        </button>
        <button onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} style={btnStyle}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="9" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="9" y1="18" x2="21" y2="18"/><circle cx="4" cy="6" r="1.5" fill="currentColor"/><circle cx="4" cy="12" r="1.5" fill="currentColor"/><circle cx="4" cy="18" r="1.5" fill="currentColor"/></svg>
        </button>
        <button onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }} style={btnStyle} title="Formatierung entfernen">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="4" x2="18" y2="4"/><line x1="6" y1="20" x2="10" y2="20"/><path d="M14 4l-4 16"/><line x1="3" y1="21" x2="21" y2="3"/></svg>
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={e => setInhalt(e.currentTarget.innerHTML)}
        style={{ minHeight: 100, padding: '12px 14px', outline: 'none', fontSize: 14, color: T.ink, lineHeight: 1.6, background: 'white', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
      />
    </div>
  );
}

/* ══════════════════════════════════════
   Main Nachrichten page
══════════════════════════════════════ */
export default function Nachrichten() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();
  const location = useLocation();

  const [loading,      setLoading]      = useState(true);

  /* Nachrichten state */
  const [konversationen,  setKonversationen]  = useState([]);
  const [nachrichten,     setNachrichten]     = useState([]);
  const [selKonv,         setSelKonv]         = useState(null);
  const [msgSearch,       setMsgSearch]       = useState('');
  const [filterGesprach,  setFilterGesprach]  = useState('');
  const [msgTyp,          setMsgTyp]          = useState('email'); // 'email' | 'portal'
  const [betreff,         setBetreff]         = useState('');
  const [inhalt,          setInhalt]          = useState('');
  const [emailAddr,       setEmailAddr]       = useState('');
  const [sending,         setSending]         = useState(false);


  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [kRes, tRes] = await Promise.all([
      supabase.from('konversationen').select('*').eq('owner_id', user.id).order('created_at', { ascending: false }),
      supabase.from('tenants').select('id,first_name,last_name,email,property_id').eq('owner_id', user.id),
    ]);
    setKonversationen(kRes.data || []);
    setTenants(tRes.data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  /* Prefill from navigation state (e.g. reminder from Tenants page) */
  const prefillHandled = useRef(false);
  useEffect(() => {
    if (prefillHandled.current || loading) return;
    const pf = location.state?.prefill;
    if (!pf) return;
    prefillHandled.current = true;
    setBetreff(pf.betreff || '');
    setInhalt(pf.inhalt?.replace(/\n/g, '<br>') || '');
    if (pf.tenantEmail) setEmailAddr(pf.tenantEmail);
    if (pf.type === 'reminder') setMsgTyp('email');
    // Create a new conversation for this reminder
    (async () => {
      const { data } = await supabase.from('konversationen').insert({
        owner_id: user.id,
        empfaenger: pf.tenantName || 'Mieter',
        tenant_id: pf.tenantId || null,
        status: 'entwurf',
        titel: pf.betreff || 'Mahnung',
      }).select().single();
      if (data) {
        setKonversationen(prev => [data, ...prev]);
        setSelKonv(data);
      }
    })();
    // Clear state so refresh doesn't re-prefill
    window.history.replaceState({}, '');
  }, [loading, location.state]);

  /* Load messages for selected conversation */
  useEffect(() => {
    if (!selKonv) return;
    supabase.from('nachrichten').select('*').eq('konversation_id', selKonv.id).order('created_at').then(({ data }) => setNachrichten(data || []));
  }, [selKonv]);

  /* Create new conversation */
  async function createKonversation() {
    const { data, error } = await supabase.from('konversationen').insert({
      owner_id: user.id, empfaenger: 'Sammelkunde/Sammellieferant', status: 'entwurf', titel: 'Neue Konversation',
    }).select().single();
    if (error) { addToast('Fehler', 'error'); return; }
    setKonversationen(p => [data, ...p]);
    setSelKonv(data);
  }

  /* Send message */
  async function sendNachricht() {
    if (!selKonv) return;
    if (!inhalt.trim() && !inhalt.replace(/<[^>]*>/g,'').trim()) { addToast('Bitte Nachricht eingeben', 'error'); return; }
    setSending(true);
    const { error } = await supabase.from('nachrichten').insert({
      konversation_id: selKonv.id, owner_id: user.id, betreff, inhalt, typ: msgTyp,
    });
    if (!error) {
      await supabase.from('konversationen').update({ status: 'offen' }).eq('id', selKonv.id);
      setBetreff(''); setInhalt('');
      const { data } = await supabase.from('nachrichten').select('*').eq('konversation_id', selKonv.id).order('created_at');
      setNachrichten(data || []);
      await load();
    }
    setSending(false);
  }

  /* Mark as solved */
  async function markGeloest() {
    if (!selKonv) return;
    await supabase.from('konversationen').update({ status: 'geloest' }).eq('id', selKonv.id);
    setSelKonv(c => ({ ...c, status: 'geloest' }));
    await load();
  }

  const filteredKonv = konversationen.filter(k =>
    !msgSearch || (k.titel || '').toLowerCase().includes(msgSearch.toLowerCase()) || (k.empfaenger || '').toLowerCase().includes(msgSearch.toLowerCase())
  );

  /* Status badge for conversation */
  const STATUS_LABELS = { entwurf: 'Entwurf', offen: 'Offen', geloest: 'Gelöst' };
  const STATUS_COLORS = { entwurf: { bg: '#f3f4f6', color: T.muted, border: '#d1d5db' }, offen: { bg: '#f5f5f5', color: T.ink, border: T.border }, geloest: { bg: '#f0fdf4', color: T.green, border: '#bbf7d0' } };

  const fmtTime = d => d ? new Date(d).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
  const fmtDate = d => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

  const showList = isMobile ? !selKonv : true;
  const showDetail = isMobile ? !!selKonv : true;

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", overflow: 'hidden' }}>

      {/* ── Left panel ── */}
      {showList && (
      <div style={{ width: isMobile ? '100%' : 300, flexShrink: 0, borderRight: isMobile ? 'none' : `1px solid ${T.border}`, background: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '20px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>Nachrichten</span>
              {konversationen.length > 0 && (
                <span style={{ fontSize: 12, fontWeight: 700, background: T.ink, color: 'white', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{konversationen.length}</span>
              )}
            </div>
            {(
              <button onClick={createKonversation} style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${T.border}`, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: T.muted, padding: 0 }}>+</button>
            )}
          </div>
          <div style={{ borderBottom: `1px solid ${T.border}`, paddingBottom: 10 }} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '14px 12px' }}>
          {/* ── Nachrichten tab ── */}
          {(
            <>
              <input value={msgSearch} onChange={e => setMsgSearch(e.target.value)} placeholder="Nachrichten durchsuchen..."
                style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: `1px solid ${T.border}`, fontSize: 13, outline: 'none', marginBottom: 10, boxSizing: 'border-box' }} />
              <select value={filterGesprach} onChange={e => setFilterGesprach(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: `1px solid ${T.border}`, fontSize: 13, outline: 'none', marginBottom: 12, background: 'white', cursor: 'pointer', color: T.ink }}>
                <option value="">Alle Gespräche</option>
                <option value="entwurf">Entwürfe</option>
                <option value="offen">Offen</option>
                <option value="geloest">Gelöst</option>
              </select>
              {filteredKonv.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: '#f7f7f7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Keine Konversationen</div>
                  <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>Erstelle eine neue Konversation mit dem + Button.</div>
                </div>
              ) : filteredKonv.map(k => {
                const sc = STATUS_COLORS[k.status] || STATUS_COLORS.entwurf;
                const sel = selKonv?.id === k.id;
                return (
                  <div key={k.id} onClick={() => { setSelKonv(k); setNachrichten([]); }}
                    style={{ padding: '12px 14px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: sel ? '#f5f5f5' : 'transparent', border: `1px solid ${sel ? T.ink : 'transparent'}` }}
                    onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg; }}
                    onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{k.titel || 'Neue Konversation'}</span>
                      {k.titel?.includes('Mahnung') || k.titel?.includes('Offene Miete') ? (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}>Mahnung</span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 12, color: T.muted, marginBottom: 6 }}>{k.empfaenger || '–'}</div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                      {STATUS_LABELS[k.status] || k.status}
                    </span>
                  </div>
                );
              })}
            </>
          )}

        </div>
      </div>
      )}

      {/* ── Right area ── */}
      {showDetail && (
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── NACHRICHTEN right ── */}
        {(
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Top bar */}
            {selKonv && (
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, padding: isMobile ? '10px 14px' : '14px 20px', borderBottom: `1px solid ${T.border}`, background: 'white', flexShrink: 0 }}>
                {isMobile && (
                  <button onClick={() => setSelKonv(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>Nachricht an: {selKonv.empfaenger || '–'}</span>
                  {emailAddr && <span style={{ fontSize: 12, color: T.muted }}>{emailAddr}</span>}
                </div>
                <div style={{ flex: 1 }} />
                <button onClick={markGeloest} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 7, border: `1px solid ${T.border}`, background: 'white', color: T.ink, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Als gelöst markieren
                </button>
                <button style={{ width: 32, height: 32, borderRadius: 7, border: `1px solid ${T.border}`, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  {[0,1,2].map(i => <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: T.muted }} />)}
                </button>
              </div>
            )}

            {/* Messages area */}
            <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
              {!selKonv ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <div style={{ textAlign: 'center', maxWidth: 480 }}>
                    <div style={{ width: 64, height: 64, borderRadius: 18, background: '#f7f7f7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: T.ink, marginBottom: 8 }}>Kommunikation verwalten</div>
                    <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.6, marginBottom: 32 }}>Wähle eine Konversation aus oder starte eine neue Unterhaltung mit deinen Mietern.</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                      {[
                        { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>, title: 'Direktnachrichten', desc: 'Schreibe Mietern direkt und behalte alles im Überblick' },
                        { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>, title: 'Ankündigungen', desc: 'Sende wichtige Infos an alle Mieter eines Objekts' },
                        { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>, title: 'Status verfolgen', desc: 'Offen, gelöst oder Entwurf – behalte den Überblick' },
                      ].map((c, i) => (
                        <div key={i} style={{ background: T.bg, borderRadius: 12, padding: '16px 14px', textAlign: 'left', border: `1px solid ${T.border}` }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f7f7f7', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>{c.icon}</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 4 }}>{c.title}</div>
                          <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>{c.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : nachrichten.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Noch keine Nachrichten</div>
                    <div style={{ fontSize: 13, color: T.muted }}>Verfasse eine Nachricht um die Konversation zu starten.</div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {nachrichten.map(m => (
                    <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.richtung === 'eingehend' ? 'flex-start' : 'flex-end' }}>
                      {m.betreff && <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>{m.betreff}</div>}
                      <div style={{ maxWidth: '70%', padding: '12px 16px', borderRadius: 12, background: m.richtung === 'eingehend' ? T.bg : '#f5f5f5', border: `1px solid ${m.richtung === 'eingehend' ? T.border : T.border}`, fontSize: 14, color: T.ink, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: m.inhalt }} />
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{fmtDate(m.created_at)} {fmtTime(m.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Compose editor */}
            {selKonv && (
              <div style={{ borderTop: `1px solid ${T.border}`, padding: '16px 24px', background: 'white', flexShrink: 0 }}>
                <Editor betreff={betreff} setBetreff={setBetreff} inhalt={inhalt} setInhalt={setInhalt} showBetreff={msgTyp === 'email'} placeholder="Nachricht verfassen..." />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                  <button style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 7, border: `1px solid ${T.border}`, background: 'white', color: T.ink, fontSize: 13, cursor: 'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    Vorlage einfügen
                  </button>
                  <button style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 7, border: `1px solid ${T.border}`, background: 'white', color: T.ink, fontSize: 13, cursor: 'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                    Dokument anhängen
                  </button>
                  <div style={{ flex: 1 }} />
                  <button onClick={sendNachricht} disabled={sending} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 20px', borderRadius: 8, border: 'none', background: T.ink, color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: sending ? 0.7 : 1 }}>
                    {sending ? 'Senden…' : 'Senden'}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Right sidebar (desktop only) ── */}
        {!isMobile && selKonv && (
          <div style={{ width: 240, flexShrink: 0, borderLeft: `1px solid ${T.border}`, background: 'white', padding: '16px', overflow: 'auto' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>An:</div>
            <input value={emailAddr} onChange={e => setEmailAddr(e.target.value)} placeholder="E-Mail-Adresse"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1.5px solid ${emailAddr ? T.ink : T.border}`, outline: 'none', fontSize: 13, boxSizing: 'border-box' }} />
            <div style={{ fontSize: 12, color: T.muted, marginTop: 10 }}>{selKonv.empfaenger || ''}</div>
          </div>
        )}

      </div>
      )}

    </div>
  );
}
